import { Component, computed, effect, input, output, signal, untracked } from '@angular/core';
import { Applicant } from 'api-clients/ai-api-client';
import { ApplicantCard } from 'src/shared/ez-card/ez-selectable-card/applicant-card/applicant-card';
import { ApplicantCardComponent } from 'src/shared/ez-card/ez-selectable-card/applicant-card/applicant-card.component';

@Component({
  selector: 'ez-eva-applicants',
  imports: [
    ApplicantCardComponent,
  ],
  templateUrl: './eva-applicants.component.html',
  styleUrl: './eva-applicants.component.scss',
})
export class EvaApplicantsComponent {
  protected readonly applicants = input.required<Applicant[]>();
  protected readonly applicantId = input<number | null>(null);
  protected readonly isHistoryLoaded = input<boolean>(false);
  protected readonly selectedApplicant = output<Applicant>();

  readonly #selectedApplicant = signal<Applicant | null>(null);
  protected readonly hasSelection = computed(() => this.#selectedApplicant() != null);
  protected readonly isSelectionDisabled = computed(() => this.isHistoryLoaded() && this.applicantId() != null);

  protected readonly applicantSelectionStates = computed(() => {
    const selected = this.#selectedApplicant();
    const hasSelection = selected != null;

    return this.applicants().map(applicant =>
      !hasSelection ? false : applicant.applicantId === selected?.applicantId ? true : undefined
    );
  });

  protected readonly cardApplicants = computed((): ApplicantCard[] =>
    this.applicants().map((applicant): ApplicantCard => ({
      applicantId: applicant.applicantId,
      displayName: applicant.displayName ?? '',
      isCommercial: applicant.isCommercial,
      applicantType: applicant.applicantType,
      dob: applicant.dob,
      primaryPhone: applicant.primaryPhone,
      email: applicant.email,
      policyCount: applicant.policyCount,
      primaryAddress: applicant.primaryAddress,
    }))
  );

  constructor() {
    // Setting any applicantId passed from parent as selected applicant, this is required for chat history restoration, close and reopen EVA sidebar etc.
    effect(() => {
      const applicantToBeSelected = this.applicantId();
      if (applicantToBeSelected != null) {
        const applicant = untracked(() => this.applicants().find(applicant => applicant.applicantId === applicantToBeSelected));
        if (applicant && untracked(() => this.#selectedApplicant())?.applicantId !== applicantToBeSelected) {
          this.#selectedApplicant.set(applicant);
        }
      }
    });
  }

  protected onApplicantSelect(applicantId: number, selected: boolean): void {
    if (this.isSelectionDisabled() || this.hasSelection()) {
      return;
    }

    if (selected) {
      const applicant = this.applicants().find(applicant => applicant.applicantId === applicantId);
      if (applicant) {
        this.#selectedApplicant.set(applicant);
        this.selectedApplicant.emit(applicant);
      }
    } else {
      this.#selectedApplicant.set(null);
    }
  }
}
